import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { GuardProvider } from 'react-router-guards'
import { RouteFactory } from './route-factory'

import MeProvider, { useMe } from '../../contexts/me'
import AuthProvider from '../../contexts/auth'
import FilterProvider from '../../contexts/filter/provider'

import { authGuard, authorityGuard, billingGuard } from './guards'

import { Authority, RoutesEnum } from '../../enums'

// Layouts base
import { BaseLayout, LoginLayout, MobileLayout } from '../layouts'

// Pages
// Doctors Pages
import * as DoctorPages from '../../../main/factories/pages/doctors'

// Contractors Pages
import * as ContractorPages from '../../../main/factories/pages/contractors'

// Common Pages
import * as CommonPages from '../../../main/factories/pages/common'

const RedirectToHome = () => {
  const history = useHistory()

  const { me } = useMe()

  useEffect(() => {
    if (me?.profiles[0].authority === Authority.DOCTOR) {
      history.push(RoutesEnum.HOME_DOCTOR)
    } else if (me?.profiles[0].authority === Authority.CONTRACTOR) {
      history.push(RoutesEnum.HOME_CONTRACTOR)
    }
  }, [history, me])
  return null
}

const Routes = (): JSX.Element => {
  const routes = [
    {
      layout: LoginLayout,
      pages: [
        {
          path: RoutesEnum.SIGNUP_DOCTOR,
          component: DoctorPages.MakeSingupDoctor,
        },
        {
          path: RoutesEnum.SIGNUP2_DOCTOR,
          component: DoctorPages.MakeSignup2Doctor,
        },
        {
          path: RoutesEnum.SIGNUP3_DOCTOR,
          component: DoctorPages.MakeSingup3Doctor,
        },
        {
          path: RoutesEnum.SIGNUP4_DOCTOR,
          component: DoctorPages.MakeSingup5Doctor,
        },
        {
          path: RoutesEnum.TERMS_DOCTOR,
          component: DoctorPages.MakeTermsDoctor,
        },

        {
          path: RoutesEnum.SIGNUP_CONTRACTOR,
          component: ContractorPages.MakeSingupContractors,
        },
        {
          path: RoutesEnum.SIGNUP2_CONTRACTOR,
          component: ContractorPages.MakeSignup2Contractors,
        },
        {
          path: RoutesEnum.SIGNUP3_CONTRACTOR,
          component: ContractorPages.MakeSignup3Contractors,
        },
        /* { path: RoutesEnum.SIGNUP4_CONTRACTOR,
        component: ContractorPages.MakeSignup5Contractors },
        do not delete this line, it'll be used in the future */
        {
          path: RoutesEnum.TERMS_CONTRACTOR,
          component: ContractorPages.MakeTermsContractors,
        },

        { path: RoutesEnum.SIGNIN, component: CommonPages.MakeSignin },
        {
          path: RoutesEnum.FORGOT_PASSWORD,
          component: CommonPages.MakeForgotPassword,
        },
        {
          path: RoutesEnum.RECOVERY_PASSWORD,
          component: CommonPages.MakeRecoveryPassword,
        },
        { path: RoutesEnum.INTRO, component: CommonPages.MakeIntro },
        {
          path: RoutesEnum.PRIVACY_POLICY,
          component: CommonPages.MakePolicyPrivacy,
        },
        {
          path: RoutesEnum.CHANGE_PASSWORD,
          component: CommonPages.MakeChangePassword,
          meta: { auth: true },
        },

        {
          path: RoutesEnum.INTRO_DOCTOR,
          component: DoctorPages.MakeIntroDoctors,
        },
        {
          path: RoutesEnum.INTRO_CONTRACTOR,
          component: ContractorPages.MakeIntroContractors,
        },
      ],
    },
    {
      layout: MobileLayout,
      pages: [
        // Doctors Pages
        {
          path: RoutesEnum.HOME_DOCTOR,
          component: DoctorPages.MakeHomeDoctor,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },
        {
          path: RoutesEnum.LIST_CONTESTS_VACANCIES_DOCTOR,
          component: DoctorPages.MakeListContestsVacancies,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },
        {
          path: RoutesEnum.CONTESTS_DOCTOR,
          component: DoctorPages.MakeContests,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },
        {
          path: RoutesEnum.VACANCIES_DOCTOR,
          component: DoctorPages.MakeVacancies,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },
        {
          path: RoutesEnum.PROFILE_DOCTOR,
          component: DoctorPages.MakeProfileDoctor,
          meta: { auth: true, authority: Authority.DOCTOR },
        },
        {
          path: RoutesEnum.EDIT_ADDRESS_DOCTOR,
          component: DoctorPages.MakeEditAddress,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },
        {
          path: RoutesEnum.EDIT_HEADER_DATA_DOCTOR,
          component: DoctorPages.MakeEditHeaderData,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },
        {
          path: RoutesEnum.EDIT_AVAILABILITY_DOCTOR,
          component: DoctorPages.MakeEditAvailability,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },
        {
          path: RoutesEnum.REQUESTS_DOCTOR,
          component: DoctorPages.MakeRequest,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },
        {
          path: RoutesEnum.HIRING_DOCTOR,
          component: DoctorPages.MakeHiring,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },
        {
          path: RoutesEnum.CHATS_DOCTOR,
          component: DoctorPages.MakeChatsDoctor,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },
        {
          path: RoutesEnum.CONTRACTOR_DOCTOR,
          component: DoctorPages.MakeContractor,
          meta: { auth: true, authority: Authority.DOCTOR, billing: true },
        },

        // Contractors Pages
        {
          path: RoutesEnum.HOME_CONTRACTOR,
          component: ContractorPages.MakeHomeContractor,
          meta: { auth: true, authority: Authority.CONTRACTOR },
        },
        {
          path: RoutesEnum.DOCTOR_CONTRACTOR,
          component: ContractorPages.MakeDoctor,
          meta: { auth: true, authority: Authority.CONTRACTOR },
        },
        {
          path: RoutesEnum.RECENT_CONTACTS_CONTRACTOR,
          component: ContractorPages.MakeRecentContacts,
          meta: { auth: true, authority: Authority.CONTRACTOR },
        },
        {
          path: RoutesEnum.CONTRACTS_CONTRACTOR,
          component: ContractorPages.MakeContracts,
          meta: { auth: true, authority: Authority.CONTRACTOR },
        },
        {
          path: RoutesEnum.RESULT_CONTRACTOR,
          component: ContractorPages.MakeResult,
          meta: { auth: true, authority: Authority.CONTRACTOR },
        },
        {
          path: RoutesEnum.PROFILE_CONTRACTOR,
          component: ContractorPages.MakeProfileContractor,
          meta: { auth: true, authority: Authority.CONTRACTOR },
        },
        {
          path: RoutesEnum.EDIT_HEADER_CONTRACTOR,
          component: ContractorPages.MakeEditHeaderContractor,
          meta: { auth: true, authority: Authority.CONTRACTOR },
        },
        {
          path: RoutesEnum.CHATS_CONTRACTOR,
          component: ContractorPages.MakeChatsContractor,
          meta: { auth: true, authority: Authority.CONTRACTOR },
        },

        // Common Pages
        {
          path: RoutesEnum.CONFIRM_ACCOUNT,
          component: CommonPages.MakeConfirmAccount,
          meta: { auth: false },
        },
        {
          path: RoutesEnum.FAQ,
          component: CommonPages.MakeFaq,
          meta: { auth: false },
        },
        {
          path: RoutesEnum.NOTIFICATIONS,
          component: CommonPages.MakeNotifications,
          meta: { auth: true },
        },
        {
          path: RoutesEnum.CHAT,
          component: CommonPages.MakeChat,
          meta: { auth: true },
        },
        {
          path: RoutesEnum.PLANS,
          component: CommonPages.MakePlans,
          meta: { auth: true, authority: Authority.DOCTOR },
        },
        {
          path: RoutesEnum.CHECKOUT,
          component: CommonPages.MakeCheckout,
          meta: { auth: true, authority: Authority.DOCTOR },
        },
        {
          path: RoutesEnum.EDIT_PROFILE_IMAGE,
          component: CommonPages.MakeEditProfileImage,
          meta: { auth: true, billing: true },
        },
      ],
    },
    {
      layout: BaseLayout,
      pages: [
        { path: '/', component: RedirectToHome, meta: { auth: true } },
        { path: '*', component: RedirectToHome },
      ],
    },
  ]

  return (
    <MeProvider>
      <AuthProvider>
        <FilterProvider>
          <GuardProvider guards={[authGuard, authorityGuard, billingGuard]}>
            <RouteFactory routes={routes} />
          </GuardProvider>
        </FilterProvider>
      </AuthProvider>
    </MeProvider>
  )
}

export default Routes
